import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { hasTab } from './Card.helpers';
import { useTabSizing } from './Card.hooks';
import ScrollableCard from './ScrollableCard';

interface Props {
	tabNames: string[];
	children: (selectedTab: string) => React.ReactNode;
	className?: string;
	outline?: boolean;
}

const TabbedCard = ({ tabNames, className, children, outline }: Props) => {
  const [selectedTab, setSelectedTab] = useState('');

  useEffect(() => {
    (!selectedTab || !hasTab(selectedTab, tabNames)) && setSelectedTab(tabNames[0]);
  }, [tabNames]);

  return (
    <div className={className}>
      <TabContainer
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        tabNames={tabNames}
        outline={outline}
      />
      <ScrollableCard className={clsx(outline && 'border border-zinc-200 mt-[-1px]')}>
        {children(selectedTab)}
      </ScrollableCard>
    </div>
  );
};

interface TabContainerProps {
	tabNames: string[];
	selectedTab: string;
	setSelectedTab: (name: string) => void;
	outline?: boolean;
}

const TabContainer = ({ selectedTab, setSelectedTab, tabNames, outline }: TabContainerProps) => {
  const tabWidthPx = useTabSizing(tabNames);
  const [position, setPosition] = useState(0);
  const scrollTabRef = useRef<HTMLDivElement>(null);
  const maxPosition = useMemo(() => {
    const totalLength = tabNames.length - 1;

    return scrollTabRef.current
      ? totalLength - (scrollTabRef.current.clientWidth / tabWidthPx - 1)
      : totalLength;
  }, [tabNames.length, scrollTabRef.current]);
  const handleArrowClick = (delta: number) => {
    if (delta === 1 && position < maxPosition) {
      setPosition((position) => position + 1);
    } else if (delta === -1 && position > 0) {
      setPosition((position) => position - 1);
    }
  };

  return (
    <div className="relative flex items-center w-full max-w-full min-w-full">
      <button
        className="inline-flex items-center justify-center w-10 overflow-hidden"
        onClick={() => handleArrowClick(-1)}
      >
        <BsChevronLeft
          className={clsx(
            'text-lg transition-transform',
            position > 0 ? 'translate-x-0' : '-translate-x-6'
          )}
        />
      </button>
      <div className="flex-1 w-full overflow-hidden rounded-t-md">
        <div
          className="transition-transform"
          style={{
            width: `${tabWidthPx * (tabNames.length + 1)}px`,
            transform: `translateX(${-((tabWidthPx + 4) * position)}px)`
          }}
          ref={scrollTabRef}
        >
          {tabNames.map((name) => (
            <button
              key={name}
              className={clsx(
                'text-sm py-2.5 tracking-wide text-center inline-block mr-1 rounded-t-md',
                selectedTab === name
                  ? 'bg-white border-t border-zinc-200 text-zinc-900'
                  : 'bg-gradient-to-b from-zinc-800 to-zinc-700 text-zinc-100',
                selectedTab === name && outline && 'border-x'
              )}
              style={{
                width: `${tabWidthPx}px`
              }}
              onClick={() => setSelectedTab(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
      <button
        className="inline-flex items-center justify-center w-10 overflow-hidden"
        onClick={() => handleArrowClick(1)}
      >
        <BsChevronRight
          className={clsx(
            'text-lg transition-transform',
            position < maxPosition ? 'translate-x-0' : 'translate-x-6'
          )}
        />
      </button>
    </div>
  );
};

const Header: React.FC<{ className?: string }> = ({ children, className }) => (
  <ScrollableCard.Header className={className}>{children}</ScrollableCard.Header>
);

const Body: React.FC<{ className?: string; maxHeightClassName?: string }> = ({
  children,
  className,
  maxHeightClassName
}) => (
  <ScrollableCard.Body className={className} maxHeightClassName={maxHeightClassName}>
    {children}
  </ScrollableCard.Body>
);

TabbedCard.Header = Header;
TabbedCard.Body = Body;

export { TabbedCard as default };
